// Zone placement selector for the exhibit form ("born in a zone").
//
// Presentational + controlled: the orchestrator (ExhibitForm) owns the
// selected zone id and builds the option labels ("zone — exhibition").
// The option list comes from the scope-filtered staff zone list, so an
// editor physically cannot pick an out-of-scope zone; the backend
// re-checks anyway (422 zone_required / 403 zone_forbidden).
//
// `allowNone` is admin-only: an editor unlinking an exhibit would strand
// it outside their own scope (backend 422 exhibit.move.unlink_admin_only).

import type { ChangeEvent, FC } from "react";
import { Label } from "@shared/components/ui/label";

export interface ZoneOption {
  id: string;
  label: string;
}

export interface ZoneSelectProps {
  options: ZoneOption[];
  /** Selected zone id; "" = no zone (only reachable when allowNone). */
  value: string;
  onChange: (zoneId: string) => void;
  /** Admin-only: offer the "— No zone (unassigned) —" option. */
  allowNone: boolean;
  disabled?: boolean;
}

const ZoneSelect: FC<ZoneSelectProps> = ({
  options,
  value,
  onChange,
  allowNone,
  disabled = false,
}) => {
  if (options.length === 0 && !allowNone) {
    return (
      <p
        className="text-sm text-muted-foreground"
        data-testid="zone-select-empty"
      >
        No zones in your scope yet — create a zone first.
      </p>
    );
  }

  const handleChange = (e: ChangeEvent<HTMLSelectElement>) =>
    onChange(e.target.value);

  return (
    <div className="space-y-2">
      <Label htmlFor="exhibit-zone-select">Zone {allowNone ? "" : "*"}</Label>
      <select
        id="exhibit-zone-select"
        value={value}
        onChange={handleChange}
        disabled={disabled}
        className="w-full border rounded px-3 py-2 disabled:bg-muted disabled:text-muted-foreground"
        data-testid="exhibit-zone-select"
      >
        {allowNone ? (
          <option value="">— No zone (unassigned) —</option>
        ) : (
          <option value="" disabled>
            Select a zone…
          </option>
        )}
        {options.map((o) => (
          <option key={o.id} value={o.id}>
            {o.label}
          </option>
        ))}
      </select>
      <p className="text-xs text-muted-foreground">
        The exhibit lives inside this zone; only zones in your scope are listed.
      </p>
    </div>
  );
};

export default ZoneSelect;
